import { useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from '../../i18n/I18nContext';
import { useToast } from '../../contexts/ToastContext';
import { IconButton } from '../../components/ui/icon-button';
import { apiGetPhrases, formatPhrasesAsTsv } from './phrase-export-store';

interface Props {
  articleId: string;
}

/**
 * Copy-phrases-to-Quizlet button. Always rendered — every article goes
 * through the shred-into-phrases step during generation, so the bundle
 * is expected to exist in S3. Older articles (pre-feature) return 404
 * from the GET endpoint, which surfaces as a toast.
 */
const PhraseExportButton: FC<Props> = ({ articleId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);

  const handleCopy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const bundle = await apiGetPhrases(articleId);
      const tsv = formatPhrasesAsTsv(bundle.phrases);
      if (!tsv) {
        showToast(t('phrases.no_phrases'), 'warning');
        return;
      }
      showToast(t('phrases.copied', { count: bundle.phrases.length }), 'success');
      navigator.clipboard.writeText(tsv).catch(() => {
        showToast(t('phrases.copy_failed'), 'error');
      });
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('phrases.copy_failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <IconButton onClick={handleCopy} disabled={busy} title={t('phrases.copy_to_quizlet')}>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="14" height="14" x="8" y="8" rx="2" />
        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
      </svg>
    </IconButton>
  );
};

export default PhraseExportButton;
